/**
 * Radial Gauge Component
 *
 * SVG circular gauge for displaying percentage values.
 * Used for KPI metrics like accuracy, efficiency, utilization.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React from 'react';
import {
  getThresholdStatus,
  STATUS_COLORS,
  StatusLevel,
} from '../../theme/dashboardTheme';

interface RadialGaugeProps {
  /** Current value (0-100 for percentage) */
  value: number;
  /** Maximum value (default: 100) */
  max?: number;
  /** Target value for color coding */
  target?: number;
  /** Explicit color override (hex color) */
  color?: string;
  /** Status level override */
  status?: StatusLevel;
  /** Size in pixels */
  size?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Show center label (default: true) */
  showLabel?: boolean;
  /** Unit string (default: '%') */
  unit?: string;
  /** Label below the value */
  subtitle?: string;
  /** Enable animation */
  animated?: boolean;
}

// Map status level to hex colors for SVG
const STATUS_HEX: Record<StatusLevel, string> = {
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  neutral: '#6b7280',
};

const RadialGauge: React.FC<RadialGaugeProps> = ({
  value,
  max = 100,
  target,
  color,
  status: statusOverride,
  size = 120,
  strokeWidth = 8,
  showLabel = true,
  unit = '%',
  subtitle,
  animated = true,
}) => {
  const percent = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  // Determine color
  let gaugeColor: string;
  if (color) {
    gaugeColor = color;
  } else if (statusOverride) {
    gaugeColor = STATUS_HEX[statusOverride];
  } else if (target !== undefined) {
    const statusLevel = getThresholdStatus(value, target);
    gaugeColor = STATUS_HEX[statusLevel];
  } else {
    gaugeColor = STATUS_HEX.info;
  }

  const center = size / 2;

  return (
    <div className="inline-flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="rgba(75, 85, 99, 0.3)"
          strokeWidth={strokeWidth}
        />
        {/* Value arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={gaugeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={animated ? 'transition-all duration-1000 ease-out' : ''}
        />
      </svg>

      {/* Center label (positioned absolutely) */}
      {showLabel && (
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{ width: size, height: size }}
        >
          <span
            className="font-bold text-white"
            style={{ fontSize: `${Math.max(size / 4.5, 14)}px` }}
          >
            {percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(1)}
          </span>
          {unit && (
            <span
              className="text-gray-400"
              style={{ fontSize: `${Math.max(size / 8, 10)}px` }}
            >
              {unit}
            </span>
          )}
        </div>
      )}

      {/* Subtitle */}
      {subtitle && (
        <span className="text-xs text-gray-400 mt-1 text-center">
          {subtitle}
        </span>
      )}
    </div>
  );
};

export default RadialGauge;
